import React, { useCallback } from 'react';
import AwesomeSlider from 'react-awesome-slider';
import 'react-awesome-slider/dist/styles.css';
import './Quiz.scss';
import Progress from '../Progress/Progress';
import MultiAnswerQuestionSlide from '../Question/MultiAnswer';
import Gauge from '../Question/Gauge';
import Tinder from '../Question/Tinder';
import Form from '../Form/Form';

// {1: {answers: {1: true, 3: true}}}
export interface QuizAnswers {
  [key: number]: {
    [key: number]: any;
  };
}

export interface Answer {
  id: number;
  answer: string;
}

export interface Question {
  id: number;
  question: string;
  answers: Answer[];
  multiple: boolean;
  notes: string;
}

export interface QuestionProps {
  question: Question;
  answers: QuizAnswers;
  nextSlide: () => void;
  onAnswerSelect: (q: number, a: number | number[], v: any) => void;
  notes: string;
}

const questions: Question[] = [
  {
    id: 1,
    question: 'What are your most immediate talent needs?',
    answers: [
      { id: 1, answer: 'Hire Salesforce-skilled talent ASAP' },
      { id: 2, answer: 'Reskill our existing employees' },
      { id: 3, answer: 'Build a long-term talent pipeline' },
    ],
    multiple: true,
    notes: 'Solid plan. Keep going!',
  },
  {
    id: 2,
    question: 'How interested are you in partnering with universities to find talent?',
    answers: [
      { id: 22, answer: 'Not interested' },
      { id: 90, answer: 'Somewhat interested' },
      { id: 160, answer: 'Very interested' },
    ],
    multiple: true,
    notes: "Thanks! Let's move on.",
  },
  {
    id: 3,
    question: 'The type of reskilling my team needs most is...',
    answers: [
      { id: 1, answer: 'Free and self-paced reskilling, earning Salesforce credentials' },
      { id: 2, answer: 'Expert-led virtual bootcamps, earning Salesforce credentials' },
      { id: 3, answer: 'To deepen their skills (most are credentialed)' },
    ],
    multiple: true,
    notes: 'Got it. Just a few more questions.',
  },
  {
    id: 4,
    question: 'WHICH OF THESE INITIATIVES ARE YOU PRIORITIZING AS YOU BUILD YOUR TALENT PIPELINE?',
    answers: [
      { id: 1, answer: 'Increasing awareness of Salesforce in my community' },
      { id: 2, answer: 'Improving inclusive hiring practices' },
      { id: 3, answer: 'Supporting local schools in training future job-seekers' },
    ],
    multiple: true,
    notes: 'Awesome! Almost finished.',
  },
  {
    id: 5,
    question: 'Which events are you most interested in?',
    answers: [
      { id: 1, answer: 'Salesforce talent thought leadership (e.g., blogs, webinars)' },
      { id: 2, answer: 'Virtual career fairs' },
      { id: 3, answer: 'Engagements with local universities (e.g., case studies)' },
    ],
    multiple: true,
    notes: 'All done! See your curated content.',
  },
];

const Quiz: React.FC<{}> = () => {
  const [currentSlide, setCurrentSlide] = React.useState<number>(0);
  const [quiz, setQuiz] = React.useState<QuizAnswers>({});

  const nextSlide: () => void = useCallback(() => {
    const aa = currentSlide + 1;
    setCurrentSlide(aa);
  }, [currentSlide]);

  const onAnswerSelect: (q: number, a: number | number[], v: any) => void = useCallback(
    (question, answer, value) => {
      // console.log('question:', question);
      // console.log('answer:', answer);
      // console.log('value:', value);

      let quizResults: QuizAnswers = { ...quiz };

      if (Array.isArray(answer)) {
        let allAnswers = {};
        answer.forEach((a) => {
          allAnswers[a] = true;
        });
        quizResults = { ...quiz, [question]: allAnswers };
      } else {
        if (quizResults[question]?.[answer]) {
          //console.log('delete:', question, answer);

          delete quizResults[question][answer];
        } else {
          //console.log('bb before:', quizResults);
          quizResults = { ...quiz, [question]: { ...quiz[question], [answer]: value } };
          //console.log('bb after:', quizResults);
        }
      }

      console.log('bb', quizResults);
      setQuiz(quizResults);
    },
    [quiz],
  );

  return (
    <div>
      <AwesomeSlider
        selected={currentSlide}
        bullets={false}
        organicArrows={false}
        mobileTouch={false}
        animation="cubeAnimation"
      >
        <div id="slide1">
          <img
            style={{ height: '6rem' }}
            alt="Salesforce"
            src="//c1.sfdcstatic.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg"
          />
          <img
            src="https://go.salesforce-partners.com/thfp-horz-logo.svg"
            alt="Trailhead Hub for Partners logo"
            style={{
              height: '6rem',
              marginLeft: '2rem',
              borderLeft: '.2rem solid #e2e8f0',
              paddingLeft: '2rem',
            }}
          />
          <h1>Talent Calibrator</h1>
          <p>
            Find proven ways to fill your talent needs.
            <br /> Answer a few questions and we’ll send you custom talent recommendations.
          </p>
          <button className={'next'} onClick={nextSlide}>
            Let's get started
          </button>
        </div>
        <div id="slide2">
          <Progress currentSlide={currentSlide} fromSlides={questions.length} />
          <MultiAnswerQuestionSlide
            answers={quiz}
            onAnswerSelect={onAnswerSelect}
            question={questions[0]}
            nextSlide={nextSlide}
            notes={questions[0].notes}
          />
        </div>

        <div id="slide3">
          <Progress currentSlide={currentSlide} fromSlides={questions.length} />
          <Gauge
            answers={quiz}
            onAnswerSelect={onAnswerSelect}
            question={questions[1]}
            nextSlide={nextSlide}
            notes={questions[1].notes}
          />
        </div>
        <div id="slide4">
          <Progress currentSlide={currentSlide} fromSlides={questions.length} />
          <MultiAnswerQuestionSlide
            answers={quiz}
            onAnswerSelect={onAnswerSelect}
            question={questions[2]}
            nextSlide={nextSlide}
            notes={questions[2].notes}
          />
        </div>
        <div id="slide5">
          <Progress currentSlide={currentSlide} fromSlides={questions.length} />
          <Tinder
            answers={quiz}
            onAnswerSelect={onAnswerSelect}
            question={questions[3]}
            nextSlide={nextSlide}
            notes={questions[3].notes}
          />
        </div>
        <div id="slide6">
          <Progress currentSlide={currentSlide} fromSlides={questions.length} />
          <MultiAnswerQuestionSlide
            answers={quiz}
            onAnswerSelect={onAnswerSelect}
            question={questions[4]}
            nextSlide={nextSlide}
            notes={questions[4].notes}
          />
        </div>
        <div id="slideForm">
          <h2>way to go!</h2>
          <p>
            You've taken a big step. Based on your answers, we've curated custom content to help you build a great
            talent program. Share your information below to get the help needed to fulfill your goals.
          </p>

          <Form data={{ questions, quiz }} />

          <p className="tiny">
            By submitting this form, you are confirming you have read and agree to our{' '}
            <a target="_blank" rel="noopener noreferrer" href="https://www.salesforce.com/company/privacy">
              Terms and Privacy Statement
            </a>
            . You are subscribing to emails from the Trailhead for Partners team only. To opt into Salesforce corporate
            email communications,{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.salesforce.com/form/other/subscribe-optin/?d=7010M000001yHmg"
            >
              click here
            </a>
            .
          </p>
        </div>
      </AwesomeSlider>
    </div>
  );
};

export default Quiz;
